/**
 * Single source of truth for niche landing identifiers and paths.
 * Import `NICHES` / `NICHE_IDS` everywhere — do not hardcode niche id or path strings.
 */

/** @typedef {{ id: string, path: string }} NicheDefinition */

/** @type {Record<string, NicheDefinition>} */
export const NICHES = Object.freeze({
    HEALTHCARE: Object.freeze({
        id: "healthcare",
        path: "/ai-marketing-for-healthcare-businesses",
    }),
    BEAUTY_WELLNESS: Object.freeze({
        id: "beauty-wellness",
        path: "/ai-marketing-for-beauty-wellness",
    }),
    FITNESS_WELLNESS: Object.freeze({
        id: "fitness-wellness",
        path: "/ai-marketing-for-fitness-wellness",
    }),
    HOSPITALITY_FOOD: Object.freeze({
        id: "hospitality-food",
        path: "/ai-marketing-for-hospitality-food-businesses",
    }),
    PROFESSIONAL_HOME_SERVICES: Object.freeze({
        id: "professional-home-services",
        path: "/ai-marketing-for-professional-home-services",
    }),
    CREATIVE_LOCAL: Object.freeze({
        id: "creative-local",
        path: "/ai-marketing-for-creative-local-businesses",
    }),
});

/** Stable niche id strings keyed by `NICHES` enum name. */
export const NICHE_IDS = Object.freeze(
    Object.fromEntries(Object.entries(NICHES).map(([key, niche]) => [key, niche.id]))
);

/** All niche definitions in display order. */
export const NICHES_LIST = Object.freeze(Object.values(NICHES));

/** All niche id strings. */
export const NICHE_ID_LIST = Object.freeze(NICHES_LIST.map((niche) => niche.id));

/** All niche URL paths. */
export const NICHE_PATH_LIST = Object.freeze(NICHES_LIST.map((niche) => niche.path));

/** @param {string} id */
export function getNicheById(id) {
    return NICHES_LIST.find((niche) => niche.id === id) ?? null;
}

/** @param {string} pathname */
export function getNicheByPath(pathname) {
    const normalized =
        pathname?.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
    return NICHES_LIST.find((niche) => niche.path === normalized) ?? null;
}

/** @param {string} value */
export function isNicheId(value) {
    return NICHE_ID_LIST.includes(value);
}

/** @param {string} value */
export function isNichePath(value) {
    return NICHE_PATH_LIST.includes(value);
}
